import { Party } from './client';
import { Roleplayer } from './roleplayer';

export enum FnaState {
  Started = 1,
  NeedsID = 2,
  Quoted = 3,
  Archived = 4,
}

export enum NeedCapacityType {
  Buy = 1,
  Sell = 2,
  BuyingAndSelling = 3,
  Renounce = 4,
}

export enum MarriageType {
  Unknown = 0,
  Community = 2,
}

/**
 * Entity type for "/FNALists" endpoint
 */
export interface FnaListItem {
  id: number;
  created: string;
  agentID: number;
  description: string;
  stateID: FnaState;
  fnaStateName: string;
  fnaStateNote: string;
  roleplayerList: string;
  isSuretyship: boolean;
  suretyshipCount: number;
  isKeyperson: boolean;
  keypersonCount: number;
  isBuyandsell: boolean;
  buyandsellCount: number;
}

export interface FnaRoleplayer extends Roleplayer {
  fnaid: number;
  fnaDescription: string;
  fnaCreated: string;
  valuationMethodID?: number;
  valuationValue?: number;
  valuationBasisID?: number;
}

export interface FnaSuretyshipNeed {
  id: number;
  disabilityBenefit: boolean;
  disabilityBenefitAmount?: number;
  coverRequired: number;
  fnaSuretyshipPartyID: number;
  netExistingProvision: number;
  estateDuty: number;
  estateDutyRate: number;
  netSurety: number;
  partyRoleplayerDOBEst: string;
  partyRoleplayerIDReg: string;
  partyRoleplayerName: string;
  partyRoleplayerTaxStatusNote: string;
  partyRoleplayerTypeNote: string;
  representativeRoleplayerName?: string;
  representativeRoleplayerTaxStatusID?: number;
  representativeRoleplayerTaxStatusNote?: string;
  representativeRoleplayerTypeID?: number;
  representativeRoleplayerTypeNote?: string;
  representativeRoleplayerIDReg?: string;
  representativeRoleplayerDOBEst?: string;
  totalSuretyship: number;
}

export interface FNASuretyshipNeeds {
  id: number;
  roleplayers: FnaRoleplayer[];
  needs: FnaSuretyshipNeed[];
}

export interface FnaKeypersonNeed {
  id: number;
  disabilityBenefit: boolean;
  disabilityBenefitAmount?: number;
  coverRequired: number;
  estateDuty: number;
  estateDutyRate: number;
  fnaKeypersonPartyID: number;
  fnaid: number;
  netExistingProvision: number;
  partyRoleplayerDOBEst: string;
  partyRoleplayerIDReg: string;
  partyRoleplayerName: string;
  partyRoleplayerTaxStatusNote: string;
  partyRoleplayerTypeNote: string;
  representativeRoleplayerName?: string;
  representativeRoleplayerTaxStatusID?: number;
  representativeRoleplayerTaxStatusNote?: string;
  representativeRoleplayerTypeID?: number;
  representativeRoleplayerTypeNote?: string;
  representativeRoleplayerIDReg?: string;
  representativeRoleplayerDOBEst?: string;
  riskTotal: number;
  section11: boolean;
  section11Amount?: number;
}

export interface FNAKeypersonNeeds {
  id: number;
  roleplayers: FnaRoleplayer[];
  needs: FnaKeypersonNeed[];
}

export interface FnaBuyAndSellNeed {
  coverRequired: number;
  deathBenefit: number;
  disabilityBenefit: boolean;
  disabilityBenefitAmount?: number;
  estateDuty: number;
  estateDutyRate: number;
  fnaBuyandsellPartyID: number;
  capacityID: NeedCapacityType;
  capacityNote: string;
  fnaid: number;
  id: number;
  interest: number;
  purchasePrice: number;
  loanaccount: number;
  existingprovision: number;
  dividendWithholdingTax: boolean;
  dividendWithholdingTaxAmount?: number;
  marriageTypeID: MarriageType;
  marriageTypeNote: string;
  netExistingProvision: number;
  partyRoleplayerDOBEst: string;
  partyRoleplayerIDReg: string;
  partyRoleplayerName: string;
  partyRoleplayerTaxStatusNote: string;
  partyRoleplayerTypeNote: string;
  representativeRoleplayerDOBEst: string;
  representativeRoleplayerIDReg: string;
  representativeRoleplayerName: string;
  representativeRoleplayerTaxStatusNote: string;
  representativeRoleplayerTypeNote: string;
  total?: {
    deathBenefit: number;
  };
  insuredLives?: {
    deathBenefit: number;
    netExistingProvision: number;
    ownership: number;
    partyRoleplayerDOBEst: string;
    partyRoleplayerIDReg: string;
    partyRoleplayerName: string;
    partyRoleplayerTaxStatusNote: string;
    partyRoleplayerTypeNote: string;
    representativeRoleplayerName?: string;
    representativeRoleplayerIDReg?: string;
    representativeRoleplayerTaxStatusNote?: string;
  }[];
  policyHolders: {
    ownership: number;
    partyRoleplayerDOBEst: string;
    partyRoleplayerIDReg: string;
    partyRoleplayerName: string;
    partyRoleplayerTaxStatusNote: string;
    partyRoleplayerTypeNote: string;
  }[];
}

export interface FnaBuyAndSellNeeds {
  id: number;
  roleplayers: FnaRoleplayer[];
  needs: FnaBuyAndSellNeed[];
}

export interface FNAParty extends Party {
  fnaid: number;
  reference: string;
  fnaPartyID: number;
  isEstateduty: boolean;
  estatedutyRate: number;
}

export interface Suretyships {
  id: number;
  fnaid: number;
  isEstateduty: boolean;
  estatedutyRate: number;
}

export interface FnaSuretyshipParty extends FNAParty {
  id: number;
  existingprovision: number;
  disabilityBenefit: boolean;
  bonds: number;
  overdrafts: number;
  leases: number;
  hirepurchases: number;
  loansLongterm: number;
  loansPersonal: number;
  creditors: number;
  other: number;
  otherDescription?: string;
}

interface Keypersons {
  id: number;
  fnaid: number;
  isEstateduty: boolean;
  estatedutyRate: number;
}

export interface FnaKeypersonParty extends FNAParty {
  id: number;
  isSection11w: boolean;
  disabilityBenefit: boolean;
  riskEmployeeRecuitmentRelocation: number;
  riskEmployeeTraining: number;
  riskProjectRestart: number;
  riskIncomeAdjustments: number;
  riskLoanAccounts: number;
  riskOther: number;
  riskOtherDescription: string;
  riskTotal: number;
  existingProvision: number;
  coverRequired: number;
}

export enum FnaPolicyStructure {
  Traditional = 1,
  Alternative = 2,
  ShareBuyBack = 3,
}

interface FnaBuyAndSell {
  id: number;
  fnaid: number;
  valuationID: number;
  policyStructureID: FnaPolicyStructure;
  policyStructureName: string;
  policyStructureNote: string;
  valuationRoleplayerID: number;
  valuationValue: number;
  valuationMethodID: number;
  valuationMethodNote: string;
  valuationBasisID: number;
  valuationBasisNote: string;
}

export interface FnaBuyAndSellParty extends FNAParty {
  fnaBuyandsellID: number;
  representativeRoleplayerID: number;
  interest: number;
  loanaccount: number;
  existingprovision: number;
  marriageTypeID: MarriageType;
  capacityID: NeedCapacityType;
  disabilityBenefit: boolean;
  dividendWithholdingTax: boolean;
  valuationID: number;
  valuationValue: number;
  valuationMethodID: number;
  valuationMethodNote: string;
  valuationBasisID: number;
  valuationBasisNote: string;
  policyStructureID: number;
  policyStructureNote: string;
}

/**
 * FNA entity with full data
 */
export interface FNADetails {
  id: number;
  agentID: number;
  description: string;
  stateID: FnaState;
  stateName: string;
  stateNote: string;
  agentsBrokerName: string;
  agentsBrokerCode: string;
  agentsPracticeCode: string;
  isBuyandsell: boolean;
  isKeyperson: boolean;
  isSuretyship: boolean;
  keypersonCount: number;
  suretyshipCount: number;
  buyandsellCount: number;
  roleplayers: FnaRoleplayer[];
  parties: FNAParty[];
  suretyships: Suretyships[];
  suretyshipParties: FnaSuretyshipParty[];
  keypersons: Keypersons[];
  keypersonParties: FnaKeypersonParty[];
  buyandsell?: FnaBuyAndSell;
  buyandsellParties: FnaBuyAndSellParty[];
}
